<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const props = defineProps({
  inbox: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const store = useStore();

const isFacebookInbox = computed(
  () => props.inbox.channel_type === INBOX_TYPES.FB
);
const isFacebookOrInstagramInbox = computed(() =>
  [INBOX_TYPES.FB, INBOX_TYPES.INSTAGRAM].includes(props.inbox.channel_type)
);

function useAiFeature(configKey, successMessageKey, errorMessageKey) {
  const enabled = ref(false);
  const prompt = ref('');
  const isSaving = ref(false);

  function loadFromInbox() {
    const config = props.inbox?.inbox_config?.[configKey] || {};
    enabled.value = !!config.enabled;
    prompt.value = config.prompt || '';
  }

  watch(() => props.inbox?.inbox_config, loadFromInbox, {
    immediate: true,
    deep: true,
  });

  const save = async () => {
    isSaving.value = true;
    try {
      await store.dispatch('inboxes/updateInbox', {
        id: props.inbox.id,
        formData: false,
        inbox_config: {
          [configKey]: {
            enabled: enabled.value,
            prompt: prompt.value,
          },
        },
      });
      useAlert(t(successMessageKey));
    } catch {
      useAlert(t(errorMessageKey));
    } finally {
      isSaving.value = false;
    }
  };

  return { enabled, prompt, isSaving, save };
}

const autoHideUser = useAiFeature(
  'auto_hide_user',
  'INBOX_MGMT.AI_INTEGRATIONS.AUTO_HIDE_USER.SAVE_SUCCESS',
  'INBOX_MGMT.AI_INTEGRATIONS.AUTO_HIDE_USER.SAVE_ERROR'
);
const autoBlockUser = useAiFeature(
  'auto_block_user',
  'INBOX_MGMT.AI_INTEGRATIONS.AUTO_BLOCK_USER.SAVE_SUCCESS',
  'INBOX_MGMT.AI_INTEGRATIONS.AUTO_BLOCK_USER.SAVE_ERROR'
);
</script>

<template>
  <div class="flex flex-col gap-4">
    <SettingsAccordion
      :title="$t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_HIDE_USER.TITLE')"
    >
      <div
        class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-amber-3 text-n-amber-11"
      >
        <Icon icon="i-lucide-info" class="size-4 shrink-0" />
        <p class="text-sm">
          {{ $t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_HIDE_USER.CHANNEL_HINT') }}
        </p>
      </div>
      <p v-if="!isFacebookOrInstagramInbox" class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_HIDE_USER.UNAVAILABLE') }}
      </p>

      <SettingsToggleSection
        v-if="isFacebookOrInstagramInbox"
        v-model="autoHideUser.enabled.value"
        :header="$t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_HIDE_USER.ENABLE.HEADER')"
        :description="
          $t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_HIDE_USER.ENABLE.DESCRIPTION')
        "
      >
        <template v-if="autoHideUser.enabled.value" #editor>
          <TextArea
            v-model="autoHideUser.prompt.value"
            auto-height
            :label="
              $t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_HIDE_USER.PROMPT.LABEL')
            "
            :placeholder="
              $t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_HIDE_USER.PROMPT.PLACEHOLDER')
            "
            min-height="8rem"
            max-height="20rem"
          />
        </template>
      </SettingsToggleSection>

      <div class="flex justify-end">
        <NextButton
          :label="$t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_HIDE_USER.SAVE')"
          :is-loading="autoHideUser.isSaving.value"
          :disabled="!isFacebookOrInstagramInbox"
          @click="autoHideUser.save"
        />
      </div>
    </SettingsAccordion>

    <SettingsAccordion
      :title="$t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_BLOCK_USER.TITLE')"
    >
      <div
        class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-amber-3 text-n-amber-11"
      >
        <Icon icon="i-lucide-info" class="size-4 shrink-0" />
        <p class="text-sm">
          {{
            $t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_BLOCK_USER.FACEBOOK_ONLY_HINT')
          }}
        </p>
      </div>
      <p v-if="!isFacebookInbox" class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_BLOCK_USER.UNAVAILABLE') }}
      </p>

      <SettingsToggleSection
        v-if="isFacebookInbox"
        v-model="autoBlockUser.enabled.value"
        :header="$t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_BLOCK_USER.ENABLE.HEADER')"
        :description="
          $t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_BLOCK_USER.ENABLE.DESCRIPTION')
        "
      >
        <template v-if="autoBlockUser.enabled.value" #editor>
          <TextArea
            v-model="autoBlockUser.prompt.value"
            auto-height
            :label="
              $t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_BLOCK_USER.PROMPT.LABEL')
            "
            :placeholder="
              $t(
                'INBOX_MGMT.AI_INTEGRATIONS.AUTO_BLOCK_USER.PROMPT.PLACEHOLDER'
              )
            "
            min-height="8rem"
            max-height="20rem"
          />
        </template>
      </SettingsToggleSection>

      <div class="flex justify-end">
        <NextButton
          :label="$t('INBOX_MGMT.AI_INTEGRATIONS.AUTO_BLOCK_USER.SAVE')"
          :is-loading="autoBlockUser.isSaving.value"
          :disabled="!isFacebookInbox"
          @click="autoBlockUser.save"
        />
      </div>
    </SettingsAccordion>
  </div>
</template>
